<template>
  <component
    :is="to ? RouterLink : 'button'"
    ref="payment-row"
    :to="to"
    :type="to ? undefined : 'button'"
    class="text-inherit col-span-full grid w-full grid-cols-2 items-start gap-x-4 gap-y-2 rounded-md bg-white p-4 text-left no-underline md:grid-cols-subgrid md:gap-6 md:gap-x-6 md:gap-y-0"
    :class="{
      'opacity-30': payment.status === PaymentStatus.CANCELLED,
      'payment-highlight-animate': isHighlighted,
    }"
  >
    <div>
      {{ formatDate(payment.date, dateFormat) }}
    </div>
    <div class="text-right">
      <Information v-if="showShareAmount">
        <template #trigger>
          <div class="flex items-center gap-2 font-bold tabular-nums">
            <InfoIcon class="size-4 text-obsidian-400" />
            {{ formatCurrency(shareAmount) }} / {{ formatCurrency(payment.amount) }}
          </div>
        </template>
        <template #modal>
          {{
            t(shareTooltipKey, {
              allocated: formatCurrency(shareAmount),
              total: formatCurrency(payment.amount),
            })
          }}
        </template>
      </Information>

      <span v-else v-currency="payment.amount" class="font-bold tabular-nums" />
    </div>
    <div class="flex h-full items-center gap-4">
      <AllocationStatusIndicator
        v-if="
          showAllocatedAmounts &&
          payment.status !== PaymentStatus.CANCELLED &&
          payment.totalAllocatedAmount != null
        "
        :is-loading="false"
        :allocated-amount="payment.totalAllocatedAmount"
        :total-amount="payment.amount"
        type="payment"
      />
    </div>
    <div>
      <PaymentStatusBadge
        v-if="
          [
            PaymentStatus.PENDING,
            PaymentStatus.AUTHORIZED,
            PaymentStatus.CANCELLED,
            PaymentStatus.CAPTURE_REQUESTED,
          ].includes(payment.status)
        "
        :status="payment.status"
        size="sm"
        short
        class="ml-2 text-sm uppercase"
      />
    </div>
    <div v-if="showAllocatedAmounts" class="flex flex-wrap gap-x-2 gap-y-0.5">
      <template
        v-if="(payment.allocatedInvoices?.length ?? 0) > 0 && invoiceAllocatedAmount === undefined"
      >
        <Information v-for="inv in payment.allocatedInvoices" :key="inv.id">
          <template #trigger>
            <router-link
              :to="{ name: 'invoice', params: { id: inv.id } }"
              class="underline hover:text-obsidian-900"
              @click.stop
            >
              {{ inv.completeInvoiceNumber ?? t('draft') }}
            </router-link>
          </template>
          <template #modal>
            {{ t('allocatedAmount', { amount: formatCurrency(inv.attributedAmount) }) }}
          </template>
        </Information>
      </template>
    </div>
    <div class="flex items-center gap-2 max-md:col-start-1 max-md:row-start-2">
      <WalletProviderIcon v-if="payment.walletProvider" :provider="payment.walletProvider" />
      {{ t(`paymentTypes.${payment.paymentType}`) }}
    </div>
    <div class="max-md:col-start-1 max-md:row-start-3">
      {{ payment.creditCardDetails?.source ?? '-' }}
    </div>
    <div class="min-w-0 break-words">
      {{ payment.referenceId || '-' }}
    </div>
  </component>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import PaymentStatusBadge from '@/components/badges/PaymentStatusBadge.vue';
import { PaymentStatus } from '@/entities/payments/payment-status.enum';
import type { Payment } from '@/entities/payments/payment.entity';
import { formatDate } from '@/hooks/use-formatted-date';
import WalletProviderIcon from '@/components/icons/WalletProviderIcon.vue';
import AllocationStatusIndicator from './AllocationStatusIndicator.vue';
import { useDateFormat } from '@/hooks/use-date-format';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import router from '@/router';
import { useRoute } from 'vue-router';
import { isClose } from '@/utils/is-zero';
import { formatCurrency } from '@/utils/format-numbers';
import Information from '@/components/Information.vue';
import { InfoIcon } from 'lucide-vue-next';
import { RouterLink, type RouteLocationRaw } from 'vue-router';

const props = defineProps<{
  payment: Payment;
  showAllocatedAmounts?: boolean;
  invoiceAllocatedAmount?: number;
  /** Portion of a payment that is not filed on this booking, in euros. */
  bookingAllocatedAmount?: number;
  to?: RouteLocationRaw;
}>();

const shareAmount = computed(
  () => props.bookingAllocatedAmount ?? props.invoiceAllocatedAmount ?? 0,
);
const showShareAmount = computed(
  () =>
    props.bookingAllocatedAmount !== undefined ||
    (props.invoiceAllocatedAmount !== undefined &&
      !isClose(props.invoiceAllocatedAmount, props.payment.amount)),
);
const shareTooltipKey = computed(() =>
  props.bookingAllocatedAmount !== undefined
    ? 'bookingAllocatedAmountTooltip'
    : 'invoiceAllocatedAmountTooltip',
);

const { t } = useI18n();
const dateFormat = useDateFormat();

const route = useRoute();
const highlightPaymentId = computed(() =>
  typeof route.query.paymentId === 'string' ? route.query.paymentId : undefined,
);
const isHighlighted = ref(false);

const handleHighlight = () => {
  isHighlighted.value = false;
  if (highlightPaymentId.value !== props.payment.id) {
    return;
  }
  setTimeout(() => {
    isHighlighted.value = true;
  }, 1);
};
watch(
  highlightPaymentId,
  () => {
    handleHighlight();
  },
  { immediate: true },
);
router.afterEach(async () => {
  handleHighlight();
});

const paymentRowRef = useTemplateRef<HTMLElement | { $el: HTMLElement }>('payment-row');

watch(
  isHighlighted,
  async () => {
    if (!isHighlighted.value) return;
    await nextTick();
    const row = paymentRowRef.value;
    const element = row instanceof HTMLElement ? row : row?.$el;
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  },
  { immediate: true },
);
</script>

<style scoped>
@keyframes payment-highlight-pulse {
  0% {
    background-color: var(--color-bittersweet-100);
    box-shadow: 0 0 0 0 var(--color-bittersweet-400);
  }
  35% {
    background-color: var(--color-bittersweet-50);
    box-shadow: 0 0 0 10px transparent;
  }
  65% {
    background-color: var(--color-bittersweet-100);
    box-shadow: 0 0 0 0 var(--color-bittersweet-400);
  }
  100% {
    background-color: var(--color-bittersweet-50);
    box-shadow: 0 0 0 8px transparent;
  }
}

@keyframes payment-highlight-fade {
  0% {
    background-color: var(--color-bittersweet-50);
    outline: 2px solid var(--color-bittersweet-400);
    outline-offset: 0;
  }
  100% {
    background-color: rgb(255 255 255);
    outline: 2px solid transparent;
    outline-offset: 0;
  }
}

.payment-highlight-animate {
  animation:
    payment-highlight-pulse 900ms ease-out 0s 2,
    payment-highlight-fade 2400ms ease-out forwards;
}
</style>

<i18n lang="json">
{
  "en": {
    "authorizedDescription": "The payment has been authorized by the customer but not yet captured.",
    "captureRequestedDescription": "The capture request has been sent to the payment provider.",
    "pendingDescription": "Confirmation for the payment is still pending.",
    "allocated": "All.",
    "invoiceAllocatedAmountTooltip": "{allocated} of {total} of the payment are allocated to this invoice.",
    "bookingAllocatedAmountTooltip": "{allocated} of {total} of the payment are allocated to this booking.",
    "draft": "Draft",
    "allocatedAmount": "Allocated: {amount}"
  },
  "de": {
    "authorizedDescription": "Die Zahlung wurde vom Kunden autorisiert, aber noch nicht eingezogen.",
    "captureRequestedDescription": "Das Abschließen / Einziehen der Zahlung wurde angefragt.",
    "pendingDescription": "Bestätigung für die Zahlung ist noch ausstehend.",
    "allocated": "Zug.",
    "invoiceAllocatedAmountTooltip": "{allocated} von insgesamt {total} der Zahlung sind dieser Rechnung zugeordnet.",
    "bookingAllocatedAmountTooltip": "{allocated} von insgesamt {total} der Zahlung sind dieser Buchung zugeordnet.",
    "draft": "Entwurf",
    "allocatedAmount": "Zugeordnet: {amount}"
  },
  "es": {
    "authorizedDescription": "El cliente ha autorizado el pago, pero aún no se ha cobrado.",
    "captureRequestedDescription": "Se ha enviado la solicitud de cobro al proveedor de pagos.",
    "pendingDescription": "La confirmación del pago sigue pendiente.",
    "allocated": "Impt.",
    "invoiceAllocatedAmountTooltip": "{allocated} de {total} del pago están imputados a esta factura.",
    "bookingAllocatedAmountTooltip": "{allocated} de {total} del pago están imputados a esta reserva.",
    "draft": "Borrador",
    "allocatedAmount": "Imputado: {amount}"
  }
}
</i18n>
